import { useState } from 'react';

export default function Home() {
  const [formData, setFormData] = useState({
    name: '',
    company: '',
    email: '',
    phone: '',
    service: '',
    message: '',
    privacyAccepted: false
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [showConfirm, setShowConfirm] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.privacyAccepted) {
      alert('プライバシーポリシーに同意してください');
      return;
    }

    setSubmitStatus('idle');
    setShowConfirm(true);
  };

  const handleConfirmedSend = async () => {
    setShowConfirm(false);
    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const formBody = new URLSearchParams();
      formBody.append('name', formData.name);
      formBody.append('company', formData.company);
      formBody.append('email', formData.email);
      formBody.append('phone', formData.phone);
      formBody.append('service', formData.service);
      formBody.append('message', formData.message);
      formBody.append('privacy', formData.privacyAccepted ? '同意する' : '未同意');

      const response = await fetch('/send.php', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formBody.toString()
      });

      const responseText = await response.text();

      if (response.ok && responseText.trim() === 'OK') {
        setSubmitStatus('success');
        setFormData({
          name: '',
          company: '',
          email: '',
          phone: '',
          service: '',
          message: '',
          privacyAccepted: false
        });
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    if (type === 'checkbox') {
      const checked = (e.target as HTMLInputElement).checked;
      setFormData(prev => ({ ...prev, [name]: checked }));
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-pink-50 to-blue-50" style={{ fontFamily: '"Noto Sans JP", sans-serif' }}>
      {/* Decorative Dots Background */}
      <div className="fixed inset-0 pointer-events-none opacity-30">
        <div className="absolute inset-0" style={{
          backgroundImage: `radial-gradient(circle, #a78bfa 2px, transparent 2px)`,
          backgroundSize: '40px 40px'
        }}></div>
      </div>

      {/* Top Navigation */}
      <nav className="relative z-50 bg-white/80 backdrop-blur-md shadow-sm">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <h1 className="text-3xl font-bold text-purple-600" style={{ fontFamily: '"Pacifico", serif' }}>VIM</h1>
            <div className="flex gap-8 items-center">
              <a href="#services" className="text-gray-700 hover:text-purple-600 transition-colors font-medium cursor-pointer">サービス</a>
              <a href="#features" className="text-gray-700 hover:text-purple-600 transition-colors font-medium cursor-pointer">特徴</a>
              <a href="#about" className="text-gray-700 hover:text-purple-600 transition-colors font-medium cursor-pointer">会社情報</a>
              <a href="#contact" className="px-6 py-2 bg-gradient-to-r from-purple-500 to-pink-500 text-white font-bold rounded-full hover:shadow-lg hover:shadow-purple-300 transition-all whitespace-nowrap cursor-pointer">
                お問い合わせ
              </a>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section - Diagonal Split */}
      <section className="relative min-h-screen flex items-center overflow-hidden">
        {/* Large Decorative Circles */}
        <div className="absolute top-20 right-20 w-96 h-96 rounded-full bg-gradient-to-br from-purple-300/40 to-pink-300/40 blur-3xl"></div>
        <div className="absolute bottom-20 left-20 w-80 h-80 rounded-full bg-gradient-to-br from-blue-300/40 to-purple-300/40 blur-3xl"></div>
        <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-64 h-64 rounded-full bg-gradient-to-br from-pink-300/30 to-purple-300/30 blur-2xl"></div>

        <div className="max-w-7xl mx-auto px-6 py-20 relative z-10">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            {/* Left Content */}
            <div>
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/60 backdrop-blur-sm rounded-full border-2 border-purple-300 mb-6">
                <div className="w-3 h-3 rounded-full bg-gradient-to-r from-purple-500 to-pink-500 animate-pulse"></div>
                <span className="text-purple-700 text-sm font-bold tracking-wider">HUMAN RESOURCE DEVELOPMENT</span>
              </div>
              
              <h2 className="text-6xl font-black text-gray-800 mb-6 leading-tight">
                人材を<span className="text-transparent bg-clip-text bg-gradient-to-r from-purple-600 to-pink-600">育て</span><br />
                営業を<span className="text-transparent bg-clip-text bg-gradient-to-r from-purple-600 to-pink-600">強化</span>し<br />
                未来を<span className="text-transparent bg-clip-text bg-gradient-to-r from-purple-600 to-pink-600">創る</span>
              </h2>
              
              <p className="text-xl text-gray-600 mb-8 leading-relaxed">
                東京都渋谷区を拠点に、人材育成と営業支援のコンサルティングを提供。実践的な研修プログラムとセミナーで、企業の成長と個人のキャリアアップを支援します。
              </p>

              <div className="flex gap-4">
                <a href="#contact" className="group px-8 py-4 bg-gradient-to-r from-purple-500 to-pink-500 text-white text-lg font-bold rounded-full hover:shadow-2xl hover:shadow-purple-300 transition-all duration-300 transform hover:scale-105 whitespace-nowrap cursor-pointer">
                  無料相談
                  <i className="ri-arrow-right-line ml-2 group-hover:translate-x-1 transition-transform inline-block"></i>
                </a>
                <a href="#services" className="px-8 py-4 bg-white/80 backdrop-blur-sm text-purple-700 text-lg font-bold rounded-full border-2 border-purple-300 hover:bg-white transition-all whitespace-nowrap cursor-pointer">
                  サービス詳細
                </a>
              </div>

              {/* Stats */}
              <div className="grid grid-cols-3 gap-6 mt-12">
                <div className="text-center">
                  <div className="text-3xl font-black text-purple-600 mb-1">実践型</div>
                  <div className="text-sm text-gray-600 font-medium">現場で活かせる支援</div>
                </div>
                <div className="text-center">
                  <div className="text-3xl font-black text-pink-600 mb-1">個別対応</div>
                  <div className="text-sm text-gray-600 font-medium">状況に合わせた提案</div>
                </div>
                <div className="text-center">
                  <div className="text-3xl font-black text-blue-600 mb-1">継続支援</div>
                  <div className="text-sm text-gray-600 font-medium">長期的な伴走体制</div>
                </div>
              </div>
            </div>

            {/* Right Image with Circles */}
            <div className="relative">
              <div className="relative rounded-[3rem] overflow-hidden shadow-2xl">
                <img 
                  src="https://readdy.ai/api/search-image?query=Colorful%20abstract%20illustration%20of%20business%20training%20and%20professional%20development%2C%20vibrant%20pastel%20colors%20including%20purple%20pink%20and%20blue%2C%20geometric%20shapes%20and%20circles%2C%20people%20silhouettes%20in%20seminar%20setting%2C%20modern%20minimalist%20art%20style%2C%20educational%20theme%20with%20books%20lightbulbs%20and%20growth%20symbols%2C%20cheerful%20and%20inspiring%20atmosphere%2C%20flat%20design%20illustration%20with%20soft%20gradients%20and%20playful%20composition&width=700&height=800&seq=hero-training-illustration-001&orientation=portrait"
                  alt="人材育成研修"
                  className="w-full h-[600px] object-cover object-top"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-purple-900/20 to-transparent"></div>
              </div>
              
              {/* Floating Circles */}
              <div className="absolute -top-8 -right-8 w-32 h-32 rounded-full bg-gradient-to-br from-purple-400 to-pink-400 opacity-80 shadow-xl flex items-center justify-center">
                <i className="ri-lightbulb-flash-line text-4xl text-white"></i>
              </div>
              <div className="absolute -bottom-8 -left-8 w-40 h-40 rounded-full bg-gradient-to-br from-blue-400 to-purple-400 opacity-80 shadow-xl flex items-center justify-center">
                <i className="ri-team-line text-5xl text-white"></i>
              </div>
              <div className="absolute top-1/2 -right-6 w-24 h-24 rounded-full bg-gradient-to-br from-pink-400 to-purple-400 opacity-80 shadow-xl flex items-center justify-center">
                <i className="ri-rocket-line text-3xl text-white"></i>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Services Section - Circular Cards */}
      <section id="services" className="py-32 relative">
        <div className="max-w-7xl mx-auto px-6 relative z-10">
          <div className="text-center mb-20">
            <span className="text-purple-600 font-bold text-sm tracking-wider uppercase mb-4 block">Our Services</span>
            <h3 className="text-5xl font-black text-gray-800 mb-6">提供サービス</h3>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              企業の成長と個人のキャリアアップを実現する、実践的なプログラム
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-12">
            {/* Service 1 */}
            <div className="group relative">
              <div className="absolute inset-0 bg-gradient-to-br from-purple-200 to-pink-200 rounded-[3rem] transform group-hover:scale-105 transition-transform duration-300"></div>
              <div className="relative bg-white/90 backdrop-blur-sm rounded-[3rem] p-10 border-4 border-purple-300 transform group-hover:-translate-y-2 transition-all duration-300">
                <div className="flex items-start gap-6 mb-6">
                  <div className="flex-shrink-0 w-24 h-24 rounded-full bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center shadow-xl">
                    <i className="ri-user-star-line text-4xl text-white"></i>
                  </div>
                  <div>
                    <h4 className="text-3xl font-black text-gray-800 mb-2">人材育成・営業支援コンサルティング</h4>
                    <div className="flex gap-2">
                      <span className="px-3 py-1 bg-purple-100 text-purple-700 text-xs font-bold rounded-full">コンサルティング</span>
                      <span className="px-3 py-1 bg-pink-100 text-pink-700 text-xs font-bold rounded-full">営業支援</span>
                    </div>
                  </div>
                </div>
                
                <p className="text-gray-600 leading-relaxed mb-6">
                  企業の課題を深く分析し、最適な人材育成プランと営業戦略を提案します。現場に即した実践的なアドバイスで、確実な成果を実現。営業チームの強化から組織全体の活性化まで、トータルでサポートいたします。
                </p>
                
                <div className="grid grid-cols-2 gap-4">
                  <div className="flex items-center gap-2 text-gray-700">
                    <div className="w-2 h-2 rounded-full bg-purple-500"></div>
                    <span className="text-sm font-medium">組織診断</span>
                  </div>
                  <div className="flex items-center gap-2 text-gray-700">
                    <div className="w-2 h-2 rounded-full bg-pink-500"></div>
                    <span className="text-sm font-medium">営業戦略立案</span>
                  </div>
                  <div className="flex items-center gap-2 text-gray-700">
                    <div className="w-2 h-2 rounded-full bg-blue-500"></div>
                    <span className="text-sm font-medium">人材育成計画</span>
                  </div>
                  <div className="flex items-center gap-2 text-gray-700">
                    <div className="w-2 h-2 rounded-full bg-purple-500"></div>
                    <span className="text-sm font-medium">継続サポート</span>
                  </div>
                </div>

                <div className="mt-8">
                  <img 
                    src="https://readdy.ai/api/search-image?query=Abstract%20illustration%20of%20business%20consulting%20and%20strategy%20planning%2C%20colorful%20pastel%20artwork%20with%20purple%20pink%20and%20blue%20tones%2C%20geometric%20shapes%20representing%20teamwork%20and%20collaboration%2C%20charts%20and%20graphs%20in%20artistic%20style%2C%20modern%20flat%20design%20with%20circles%20and%20dots%20pattern%2C%20professional%20business%20theme%2C%20inspiring%20and%20creative%20composition%2C%20minimalist%20illustration%20with%20vibrant%20gradients&width=600&height=300&seq=service-consulting-illustration-001&orientation=landscape"
                    alt="コンサルティング"
                    className="w-full h-48 object-cover object-top rounded-2xl"
                  />
                </div>
              </div>
            </div>

            {/* Service 2 */}
            <div className="group relative">
              <div className="absolute inset-0 bg-gradient-to-br from-blue-200 to-purple-200 rounded-[3rem] transform group-hover:scale-105 transition-transform duration-300"></div>
              <div className="relative bg-white/90 backdrop-blur-sm rounded-[3rem] p-10 border-4 border-blue-300 transform group-hover:-translate-y-2 transition-all duration-300">
                <div className="flex items-start gap-6 mb-6">
                  <div className="flex-shrink-0 w-24 h-24 rounded-full bg-gradient-to-br from-blue-500 to-purple-500 flex items-center justify-center shadow-xl">
                    <i className="ri-presentation-line text-4xl text-white"></i>
                  </div>
                  <div>
                    <h4 className="text-3xl font-black text-gray-800 mb-2">研修・セミナー企画運営</h4>
                    <div className="flex gap-2">
                      <span className="px-3 py-1 bg-blue-100 text-blue-700 text-xs font-bold rounded-full">研修</span>
                      <span className="px-3 py-1 bg-purple-100 text-purple-700 text-xs font-bold rounded-full">教育事業</span>
                    </div>
                  </div>
                </div>
                
                <p className="text-gray-600 leading-relaxed mb-6">
                  新入社員研修から管理職向けマネジメント研修まで、幅広いプログラムをご用意。参加者の成長を実感できる、実践的で効果的な研修を企画・運営します。オンライン・オフライン両対応で、柔軟に実施可能です。
                </p>
                
                <div className="grid grid-cols-2 gap-4">
                  <div className="flex items-center gap-2 text-gray-700">
                    <div className="w-2 h-2 rounded-full bg-blue-500"></div>
                    <span className="text-sm font-medium">新入社員研修</span>
                  </div>
                  <div className="flex items-center gap-2 text-gray-700">
                    <div className="w-2 h-2 rounded-full bg-purple-500"></div>
                    <span className="text-sm font-medium">営業スキル研修</span>
                  </div>
                  <div className="flex items-center gap-2 text-gray-700">
                    <div className="w-2 h-2 rounded-full bg-pink-500"></div>
                    <span className="text-sm font-medium">管理職研修</span>
                  </div>
                  <div className="flex items-center gap-2 text-gray-700">
                    <div className="w-2 h-2 rounded-full bg-blue-500"></div>
                    <span className="text-sm font-medium">カスタム研修</span>
                  </div>
                </div>

                <div className="mt-8">
                  <img 
                    src="https://readdy.ai/api/search-image?query=Colorful%20abstract%20illustration%20of%20training%20seminar%20and%20workshop%2C%20vibrant%20pastel%20colors%20with%20blue%20purple%20and%20pink%2C%20people%20silhouettes%20learning%20and%20presenting%2C%20educational%20symbols%20like%20books%20presentation%20screens%20and%20lightbulbs%2C%20modern%20flat%20design%20with%20geometric%20shapes%20and%20circles%2C%20inspiring%20professional%20development%20theme%2C%20cheerful%20artistic%20composition%20with%20soft%20gradients%20and%20playful%20elements&width=600&height=300&seq=service-training-illustration-001&orientation=landscape"
                    alt="研修セミナー"
                    className="w-full h-48 object-cover object-top rounded-2xl"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section - Dotted Cards */}
      <section id="features" className="py-32 bg-white/50 backdrop-blur-sm">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-20">
            <span className="text-purple-600 font-bold text-sm tracking-wider uppercase mb-4 block">Why Choose Us</span>
            <h3 className="text-5xl font-black text-gray-800 mb-6">VIMの強み</h3>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {[
              {
                icon: 'ri-focus-3-line',
                title: '実践重視',
                description: '理論だけでなく、現場で即活用できる実践的なスキルを習得',
                color: 'from-purple-400 to-pink-400',
                bgColor: 'bg-purple-50',
                image: 'https://readdy.ai/api/search-image?query=Abstract%20illustration%20of%20hands-on%20practical%20training%2C%20colorful%20pastel%20artwork%20with%20purple%20and%20pink%20tones%2C%20people%20practicing%20and%20learning%20actively%2C%20geometric%20shapes%20and%20circles%20representing%20skill%20development%2C%20modern%20flat%20design%20style%2C%20educational%20theme%20with%20action%20and%20movement%2C%20inspiring%20composition%20with%20vibrant%20gradients%2C%20minimalist%20business%20illustration&width=400&height=300&seq=feature-practical-illustration-001&orientation=landscape'
              },
              {
                icon: 'ri-user-heart-line',
                title: '個別最適化',
                description: '企業や個人のニーズに合わせた、オーダーメイドのプログラム',
                color: 'from-blue-400 to-purple-400',
                bgColor: 'bg-blue-50',
                image: 'https://readdy.ai/api/search-image?query=Abstract%20illustration%20of%20personalized%20customization%20and%20individual%20attention%2C%20colorful%20pastel%20artwork%20with%20blue%20and%20purple%20tones%2C%20one-on-one%20mentoring%20concept%2C%20geometric%20shapes%20representing%20tailored%20solutions%2C%20modern%20flat%20design%20with%20circles%20and%20dots%2C%20professional%20development%20theme%2C%20caring%20and%20supportive%20atmosphere%2C%20vibrant%20gradients%20and%20playful%20composition&width=400&height=300&seq=feature-custom-illustration-001&orientation=landscape'
              },
              {
                icon: 'ri-line-chart-line',
                title: '継続フォロー',
                description: '研修後のフォローアップを通じて、継続的な成長を支援',
                color: 'from-pink-400 to-purple-400',
                bgColor: 'bg-pink-50',
                image: 'https://readdy.ai/api/search-image?query=Abstract%20illustration%20of%20business%20growth%20and%20success%2C%20colorful%20pastel%20artwork%20with%20pink%20and%20purple%20tones%2C%20upward%20trending%20charts%20and%20graphs%20in%20artistic%20style%2C%20achievement%20and%20progress%20symbols%2C%20modern%20flat%20design%20with%20geometric%20shapes%20and%20circles%2C%20celebratory%20and%20positive%20theme%2C%20inspiring%20composition%20with%20vibrant%20gradients%2C%20minimalist%20business%20success%20illustration&width=400&height=300&seq=feature-results-illustration-001&orientation=landscape'
              }
            ].map((feature, index) => (
              <div key={index} className="group relative">
                <div className={`absolute inset-0 ${feature.bgColor} rounded-[2rem] transform group-hover:scale-105 transition-transform duration-300`}></div>
                <div className="relative bg-white/80 backdrop-blur-sm rounded-[2rem] p-8 border-4 border-dashed border-gray-300 group-hover:border-purple-400 transition-all duration-300">
                  <div className={`w-20 h-20 rounded-full bg-gradient-to-br ${feature.color} flex items-center justify-center mb-6 shadow-lg mx-auto`}>
                    <i className={`${feature.icon} text-3xl text-white`}></i>
                  </div>
                  
                  <h4 className="text-2xl font-black text-gray-800 mb-4 text-center">{feature.title}</h4>
                  <p className="text-gray-600 text-center mb-6 leading-relaxed">{feature.description}</p>
                  
                  <div className="rounded-xl overflow-hidden">
                    <img 
                      src={feature.image}
                      alt={feature.title}
                      className="w-full h-40 object-cover object-top"
                    />
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Process Section - Circular Timeline */}
      <section className="py-32 relative">
        <div className="max-w-6xl mx-auto px-6">
          <div className="text-center mb-20">
            <span className="text-purple-600 font-bold text-sm tracking-wider uppercase mb-4 block">Process</span>
            <h3 className="text-5xl font-black text-gray-800 mb-6">ご利用の流れ</h3>
          </div>

          <div className="grid md:grid-cols-4 gap-8">
            {[
              {
                step: '01',
                title: 'ヒアリング',
                description: '課題や目標を詳しくお伺いします',
                icon: 'ri-chat-smile-3-line',
                color: 'from-purple-400 to-pink-400'
              },
              {
                step: '02',
                title: 'プラン提案',
                description: '最適なプログラムをご提案',
                icon: 'ri-file-list-3-line',
                color: 'from-pink-400 to-purple-400'
              },
              {
                step: '03',
                title: '研修実施',
                description: '実践的な研修を展開',
                icon: 'ri-presentation-line',
                color: 'from-blue-400 to-purple-400'
              },
              {
                step: '04',
                title: 'フォロー',
                description: '継続的なサポートを提供',
                icon: 'ri-customer-service-2-line',
                color: 'from-purple-400 to-blue-400'
              }
            ].map((process, index) => (
              <div key={index} className="relative">
                <div className="text-center">
                  <div className={`w-32 h-32 rounded-full bg-gradient-to-br ${process.color} flex items-center justify-center mb-6 shadow-2xl mx-auto transform hover:scale-110 transition-transform duration-300`}>
                    <i className={`${process.icon} text-5xl text-white`}></i>
                  </div>
                  <div className="text-4xl font-black text-purple-300 mb-3">{process.step}</div>
                  <h4 className="text-xl font-black text-gray-800 mb-2">{process.title}</h4>
                  <p className="text-gray-600 text-sm">{process.description}</p>
                </div>
                {index < 3 && (
                  <div className="hidden md:block absolute top-16 left-full w-full h-1 bg-gradient-to-r from-purple-300 to-pink-300 -z-10"></div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="py-32 bg-gradient-to-br from-purple-100 via-pink-100 to-blue-100 relative overflow-hidden">
        {/* Decorative Circles */}
        <div className="absolute top-10 right-10 w-64 h-64 rounded-full bg-gradient-to-br from-purple-300/30 to-pink-300/30 blur-3xl"></div>
        <div className="absolute bottom-10 left-10 w-64 h-64 rounded-full bg-gradient-to-br from-blue-300/30 to-purple-300/30 blur-3xl"></div>

        <div className="max-w-4xl mx-auto px-6 relative z-10">
          <div className="text-center mb-16">
            <span className="text-purple-600 font-bold text-sm tracking-wider uppercase mb-4 block">Contact Us</span>
            <h3 className="text-5xl font-black text-gray-800 mb-6">お問い合わせ</h3>
            <p className="text-xl text-gray-600">
              人材育成・営業支援に関するご相談は、お気軽にお問い合わせください
            </p>
          </div>

          <div className="bg-white/80 backdrop-blur-lg rounded-[3rem] p-10 border-4 border-purple-300 shadow-2xl">
            <form onSubmit={handleSubmit} data-readdy-form id="vim-contact-form">
              <div className="grid md:grid-cols-2 gap-6 mb-6">
                <div>
                  <label className="block text-sm font-bold text-gray-700 mb-2">お名前 <span className="text-purple-600">*</span></label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 bg-purple-50 border-2 border-purple-200 rounded-2xl text-gray-800 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-purple-500 transition-all text-sm"
                    placeholder="山田 太郎"
                  />
                </div>
                <div>
                  <label className="block text-sm font-bold text-gray-700 mb-2">会社名 <span className="text-purple-600">*</span></label>
                  <input
                    type="text"
                    name="company"
                    value={formData.company}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 bg-purple-50 border-2 border-purple-200 rounded-2xl text-gray-800 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-purple-500 transition-all text-sm"
                    placeholder="株式会社〇〇"
                  />
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-6 mb-6">
                <div>
                  <label className="block text-sm font-bold text-gray-700 mb-2">メールアドレス <span className="text-purple-600">*</span></label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 bg-purple-50 border-2 border-purple-200 rounded-2xl text-gray-800 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-purple-500 transition-all text-sm"
                    placeholder="example@company.com"
                  />
                </div>
                <div>
                  <label className="block text-sm font-bold text-gray-700 mb-2">電話番号</label>
                  <input
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    className="w-full px-4 py-3 bg-purple-50 border-2 border-purple-200 rounded-2xl text-gray-800 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-purple-500 transition-all text-sm"
                    placeholder="03-1234-5678"
                  />
                </div>
              </div>

              <div className="mb-6">
                <label className="block text-sm font-bold text-gray-700 mb-2">ご希望のサービス <span className="text-purple-600">*</span></label>
                <select
                  name="service"
                  value={formData.service}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 pr-8 bg-purple-50 border-2 border-purple-200 rounded-2xl text-gray-800 focus:outline-none focus:ring-2 focus:ring-purple-500 transition-all text-sm cursor-pointer appearance-none"
                  style={{
                    backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 24 24' stroke='%23a78bfa'%3E%3Cpath stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='M19 9l-7 7-7-7'%3E%3C/path%3E%3C/svg%3E")`,
                    backgroundRepeat: 'no-repeat',
                    backgroundPosition: 'right 0.75rem center',
                    backgroundSize: '1.5em 1.5em'
                  }}
                >
                  <option value="" className="bg-white">選択してください</option>
                  <option value="人材育成コンサルティング" className="bg-white">人材育成コンサルティング</option>
                  <option value="営業支援コンサルティング" className="bg-white">営業支援コンサルティング</option>
                  <option value="研修・セミナー企画" className="bg-white">研修・セミナー企画</option>
                  <option value="新入社員研修" className="bg-white">新入社員研修</option>
                  <option value="管理職研修" className="bg-white">管理職研修</option>
                  <option value="その他・お見積もり" className="bg-white">その他・お見積もり</option>
                </select>
              </div>

              <div className="mb-6">
                <label className="block text-sm font-bold text-gray-700 mb-2">お問い合わせ内容 <span className="text-purple-600">*</span></label>
                <textarea
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  required
                  maxLength={500}
                  rows={5}
                  className="w-full px-4 py-3 bg-purple-50 border-2 border-purple-200 rounded-2xl text-gray-800 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-purple-500 transition-all resize-none text-sm"
                  placeholder="研修内容や人材育成に関するご相談をご記入ください（500文字以内）"
                ></textarea>
                <div className="text-right text-sm text-gray-500 mt-1">{formData.message.length}/500</div>
              </div>

              <div className="mb-8">
                <label className="flex items-start gap-3 cursor-pointer group">
                  <input
                    type="checkbox"
                    name="privacyAccepted"
                    checked={formData.privacyAccepted}
                    onChange={handleChange}
                    required
                    className="mt-1 w-5 h-5 rounded border-purple-300 bg-purple-50 text-purple-600 focus:ring-2 focus:ring-purple-500 cursor-pointer"
                  />
                  <span className="text-sm text-gray-600 leading-relaxed group-hover:text-gray-800 transition-colors">
                    <a href="/privacy" className="text-purple-600 hover:text-purple-700 underline">プライバシーポリシー</a>に同意します <span className="text-purple-600">*</span>
                  </span>
                </label>
              </div>

              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full py-4 bg-gradient-to-r from-purple-500 to-pink-500 text-white text-lg font-bold rounded-full hover:shadow-2xl hover:shadow-purple-300 transition-all duration-300 transform hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer"
              >
                {isSubmitting ? '送信中...' : '送信する'}
              </button>
            </form>
          </div>
        </div>
      </section>

      {/* Company Info Section */}
      <section id="about" className="py-32 bg-white/50 backdrop-blur-sm">
        <div className="max-w-5xl mx-auto px-6">
          <div className="bg-gradient-to-br from-purple-50 to-pink-50 rounded-[3rem] p-12 border-4 border-purple-200">
            <h3 className="text-4xl font-black text-gray-800 mb-12 text-center">会社情報</h3>
            <div className="grid md:grid-cols-2 gap-8">
              <div className="flex gap-4">
                <div className="flex-shrink-0">
                  <div className="w-16 h-16 rounded-full bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center shadow-lg">
                    <i className="ri-building-line text-2xl text-white"></i>
                  </div>
                </div>
                <div>
                  <div className="text-sm text-gray-500 mb-1 font-medium">会社名</div>
                  <div className="text-xl font-bold text-gray-800">合同会社VIM</div>
                </div>
              </div>
              <div className="flex gap-4">
                <div className="flex-shrink-0">
                  <div className="w-16 h-16 rounded-full bg-gradient-to-br from-blue-500 to-purple-500 flex items-center justify-center shadow-lg">
                    <i className="ri-calendar-line text-2xl text-white"></i>
                  </div>
                </div>
                <div>
                  <div className="text-sm text-gray-500 mb-1 font-medium">設立</div>
                  <div className="text-xl font-bold text-gray-800">令和6年7月5日</div>
                </div>
              </div>
              <div className="flex gap-4 md:col-span-2">
                <div className="flex-shrink-0">
                  <div className="w-16 h-16 rounded-full bg-gradient-to-br from-pink-500 to-purple-500 flex items-center justify-center shadow-lg">
                    <i className="ri-map-pin-line text-2xl text-white"></i>
                  </div>
                </div>
                <div>
                  <div className="text-sm text-gray-500 mb-1 font-medium">所在地</div>
                  <div className="text-xl font-bold text-gray-800">東京都渋谷区本町三丁目5番2-501号</div>
                </div>
              </div>
              <div className="flex gap-4">
                <div className="flex-shrink-0">
                  <div className="w-16 h-16 rounded-full bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center shadow-lg">
                    <i className="ri-user-line text-2xl text-white"></i>
                  </div>
                </div>
                <div>
                  <div className="text-sm text-gray-500 mb-1 font-medium">代表者</div>
                  <div className="text-xl font-bold text-gray-800">林 起基</div>
                </div>
              </div>
              <div className="flex gap-4">
                <div className="flex-shrink-0">
                  <div className="w-16 h-16 rounded-full bg-gradient-to-br from-blue-500 to-purple-500 flex items-center justify-center shadow-lg">
                    <i className="ri-phone-line text-2xl text-white"></i>
                  </div>
                </div>
                <div>
                  <div className="text-sm text-gray-500 mb-1 font-medium">電話番号</div>
                  <div className="text-xl font-bold text-gray-800">050-8895-4340</div>
                </div>
              </div>
              <div className="flex gap-4 md:col-span-2">
                <div className="flex-shrink-0">
                  <div className="w-16 h-16 rounded-full bg-gradient-to-br from-pink-500 to-blue-500 flex items-center justify-center shadow-lg">
                    <i className="ri-mail-line text-2xl text-white"></i>
                  </div>
                </div>
                <div>
                  <div className="text-sm text-gray-500 mb-1 font-medium">メールアドレス</div>
                  <div className="text-xl font-bold text-gray-800 break-all">account.v@vim-email.com</div>
                </div>
              </div>
              <div className="flex gap-4 md:col-span-2">
                <div className="flex-shrink-0">
                  <div className="w-16 h-16 rounded-full bg-gradient-to-br from-purple-500 to-blue-500 flex items-center justify-center shadow-lg">
                    <i className="ri-briefcase-line text-2xl text-white"></i>
                  </div>
                </div>
                <div>
                  <div className="text-sm text-gray-500 mb-1 font-medium">事業内容</div>
                  <div className="text-lg text-gray-700 space-y-2">
                    <div className="flex items-center gap-2">
                      <div className="w-2 h-2 rounded-full bg-purple-500"></div>
                      <span>人材育成及び営業支援に関するコンサルティング業務</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="w-2 h-2 rounded-full bg-pink-500"></div>
                      <span>人材育成のための研修、各種セミナーの企画及び運営並びに教育事業</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gradient-to-br from-purple-900 to-pink-900 text-white py-12">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center">
            <div className="text-4xl font-bold text-white mb-4" style={{ fontFamily: '"Pacifico", serif' }}>VIM</div>
            <p className="text-purple-200 mb-8">人材を育て、営業を強化し、未来を創る</p>
            <div className="flex justify-center gap-8 mb-8">
              <a href="#services" className="text-purple-200 hover:text-white transition-colors cursor-pointer">サービス</a>
              <a href="#features" className="text-purple-200 hover:text-white transition-colors cursor-pointer">特徴</a>
              <a href="#about" className="text-purple-200 hover:text-white transition-colors cursor-pointer">会社情報</a>
              <a href="#contact" className="text-purple-200 hover:text-white transition-colors cursor-pointer">お問い合わせ</a>
              <a href="/privacy" className="text-purple-200 hover:text-white transition-colors cursor-pointer">プライバシーポリシー</a>
            </div>
            <div className="border-t border-purple-700 pt-8">
              <p className="text-purple-300 text-sm">
                &copy; 2024 合同会社VIM. All rights reserved.
              </p>
            </div>
          </div>
        </div>
      </footer>

      {showConfirm && (
        <div className="fixed inset-0 z-[100] bg-slate-950/55 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="w-full max-w-2xl bg-white rounded-[2rem] shadow-2xl border border-purple-100 overflow-hidden">
            <div className="px-8 pt-8 pb-6 border-b border-purple-100">
              <div className="text-sm font-bold tracking-[0.3em] text-purple-500 uppercase mb-2">Confirm</div>
              <h4 className="text-3xl font-black text-gray-800 mb-2">入力内容の確認</h4>
              <p className="text-gray-600">送信前に内容をご確認ください。</p>
            </div>
            <div className="px-8 py-6 space-y-4 max-h-[60vh] overflow-y-auto">
              {[
                ['お名前', formData.name],
                ['会社名', formData.company],
                ['メールアドレス', formData.email],
                ['電話番号', formData.phone || '未入力'],
                ['ご希望のサービス', formData.service],
                ['お問い合わせ内容', formData.message],
              ].map(([label, value]) => (
                <div key={label} className="rounded-2xl bg-purple-50/70 border border-purple-100 p-4">
                  <div className="text-xs font-bold tracking-[0.2em] text-purple-500 uppercase mb-2">{label}</div>
                  <div className="text-gray-800 whitespace-pre-wrap break-words">{value}</div>
                </div>
              ))}
            </div>
            <div className="px-8 py-6 bg-white border-t border-purple-100 flex flex-col-reverse sm:flex-row gap-3 justify-end">
              <button
                type="button"
                onClick={() => setShowConfirm(false)}
                className="px-6 py-3 rounded-full border-2 border-gray-200 text-gray-700 font-bold hover:bg-gray-50 transition-colors whitespace-nowrap cursor-pointer"
              >
                戻る
              </button>
              <button
                type="button"
                onClick={handleConfirmedSend}
                className="px-6 py-3 rounded-full bg-gradient-to-r from-purple-500 to-pink-500 text-white font-bold hover:shadow-lg hover:shadow-purple-200 transition-all whitespace-nowrap cursor-pointer"
              >
                この内容で送信
              </button>
            </div>
          </div>
        </div>
      )}

      {isSubmitting && (
        <div className="fixed inset-0 z-[110] bg-slate-950/55 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="w-full max-w-md bg-white rounded-[2rem] shadow-2xl border border-purple-100 px-8 py-10 text-center">
            <div className="w-16 h-16 mx-auto mb-6 rounded-full border-4 border-purple-200 border-t-purple-500 animate-spin"></div>
            <div className="text-sm font-bold tracking-[0.3em] text-purple-500 uppercase mb-2">Sending</div>
            <h4 className="text-3xl font-black text-gray-800 mb-3">送信中です</h4>
            <p className="text-gray-600">内容を送信しています。しばらくお待ちください。</p>
          </div>
        </div>
      )}

      {submitStatus === 'success' && !isSubmitting && (
        <div className="fixed inset-0 z-[120] bg-slate-950/55 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="w-full max-w-md bg-white rounded-[2rem] shadow-2xl border border-emerald-100 px-8 py-10 text-center">
            <div className="w-16 h-16 mx-auto mb-6 rounded-full bg-emerald-100 flex items-center justify-center">
              <i className="ri-checkbox-circle-fill text-4xl text-emerald-500"></i>
            </div>
            <div className="text-sm font-bold tracking-[0.3em] text-emerald-500 uppercase mb-2">Completed</div>
            <h4 className="text-3xl font-black text-gray-800 mb-3">送信完了</h4>
            <p className="text-gray-600 mb-6">お問い合わせを受け付けました。担当者より折り返しご連絡いたします。</p>
            <button
              type="button"
              onClick={() => setSubmitStatus('idle')}
              className="px-6 py-3 rounded-full bg-gradient-to-r from-purple-500 to-pink-500 text-white font-bold hover:shadow-lg hover:shadow-purple-200 transition-all whitespace-nowrap cursor-pointer"
            >
              閉じる
            </button>
          </div>
        </div>
      )}

      {submitStatus === 'error' && !isSubmitting && (
        <div className="fixed inset-0 z-[120] bg-slate-950/55 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="w-full max-w-md bg-white rounded-[2rem] shadow-2xl border border-red-100 px-8 py-10 text-center">
            <div className="w-16 h-16 mx-auto mb-6 rounded-full bg-red-100 flex items-center justify-center">
              <i className="ri-error-warning-fill text-4xl text-red-500"></i>
            </div>
            <div className="text-sm font-bold tracking-[0.3em] text-red-500 uppercase mb-2">Error</div>
            <h4 className="text-3xl font-black text-gray-800 mb-3">送信に失敗しました</h4>
            <p className="text-gray-600 mb-6">時間をおいて再度お試しください。</p>
            <button
              type="button"
              onClick={() => setSubmitStatus('idle')}
              className="px-6 py-3 rounded-full border-2 border-gray-200 text-gray-700 font-bold hover:bg-gray-50 transition-colors whitespace-nowrap cursor-pointer"
            >
              閉じる
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
